import { gql, useApolloClient } from "@apollo/client";
import { EventInput } from "@fullcalendar/core";
import {
  Button,
  Checkbox,
  Link,
  LoadingIndicator,
  TextField,
} from "@resource/atlas";
import { atlasSearch, serviceGoogle } from "@resource/atlas/icons";
import { Auth0Connection, GoogleScopeEnum, strings } from "@resource/common";
import { useLogEvent } from "analytics";
import openPopUp from "auth/loginWithPopUp";
import clsx from "clsx";
import {
  CanAccessCalendar,
  GetOverlayCalendars,
  GetOverlayCalendars_getCalendarsV2 as Calendar,
  GetOverlayCalendarsVariables,
  GetUserCalendarEvents,
  GetUserCalendarEvents_getUserCalendarEvents as UserCalendarEvent,
  GetUserCalendarEventsVariables,
} from "generated/schemaTypes";
import { sortBy } from "lodash";
import { DateTime } from "luxon";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import useDebouncedSearch from "react-hooks/useDebouncedSearch";
import useQuery from "utils/useQuery";

const LIMIT = 100;

const CAN_ACCESS_CALENDAR = gql`
  query CanAccessCalendar {
    currentUserPrisma {
      id
      canAccessCalendar
    }
  }
`;

const GET_OVERLAY_CALENDARS = gql`
  query GetOverlayCalendars($where: GetCalendarsV2WhereInput!, $limit: Int!) {
    getCalendarsV2(where: $where, limit: $limit) {
      id
      name
    }
  }
`;

const GET_USER_CALENDAR_EVENTS = gql`
  query GetUserCalendarEvents(
    $ids: [String!]!
    $startTime: Int!
    $endTime: Int!
  ) {
    getUserCalendarEvents(ids: $ids, startTime: $startTime, endTime: $endTime) {
      id
      title
      startTime
      endTime
      calendarId
    }
  }
`;

const COLORS = [
  "#fff8e5",
  "#ffeaee",
  "#ffdac9",
  "#e5f7f2",
  "#e9e7ec",
  "#f1edff",
  "#e5f0ff",
];

type OverlayCalendarProps = {
  className?: string;
  onChange: Dispatch<SetStateAction<EventInput[]>>;
};
export function OverlayCalendars({
  onChange,
  className,
}: OverlayCalendarProps) {
  const apolloClient = useApolloClient();
  const [takingPermission, setTakingPermission] = useState(false);
  const [events, setEvents] = useState<UserCalendarEvent[]>([]);
  const { searchTerm, setSearchTerm, debouncedTerm } = useDebouncedSearch("");
  const [loadingEvents, setLoadingEvents] = useState<string[]>([]);
  const [selected, setSelected] = useState<(Calendar & { color?: string })[]>(
    []
  );
  const { data: userAccessData, refetch: refetchAccess } =
    useQuery<CanAccessCalendar>(CAN_ACCESS_CALENDAR);
  const { data, loading } = useQuery<
    GetOverlayCalendars,
    GetOverlayCalendarsVariables
  >(GET_OVERLAY_CALENDARS, {
    variables: {
      where: {
        search: debouncedTerm,
      },
      limit: LIMIT,
    },
    skip: !userAccessData?.currentUserPrisma?.canAccessCalendar,
  });

  const logEvent = useLogEvent({
    component: "OverlayCalendars",
    project: "Overlay Calendars When Adding Request Availability Timeslots",
  });

  const takeCalendarPermission = useCallback(() => {
    logEvent("Connect Google Calendar Clicked");
    setTakingPermission(true);
    openPopUp(
      {
        connection: Auth0Connection.google,
        scope: GoogleScopeEnum.Calendar,
      },
      async () => {
        await refetchAccess();
        setTakingPermission(false);
      }
    );
  }, [refetchAccess, logEvent]);

  const loadEvents = useCallback(
    async (id: string) => {
      logEvent("Requested Overlay Calendar Events");
      setLoadingEvents((ids) => [...ids, id]);
      const result = await apolloClient.query<
        GetUserCalendarEvents,
        GetUserCalendarEventsVariables
      >({
        query: GET_USER_CALENDAR_EVENTS,
        variables: {
          ids: [id],
          startTime: Math.round(DateTime.local().toSeconds()),
          endTime: Math.round(DateTime.local().plus({ weeks: 4 }).toSeconds()),
        },
      });
      setEvents((existing) => [
        ...existing.filter((event) => event.calendarId !== id),
        ...result.data.getUserCalendarEvents,
      ]);
      setLoadingEvents((ids) => ids.filter((i) => i !== id));
    },
    [apolloClient, logEvent]
  );

  useEffect(() => {
    const mappedEvents: EventInput[] = [];
    let orderOffset = 0;
    const sortedEvents = sortBy(events, "startTime");
    for (const calendar of selected) {
      for (const event of sortedEvents) {
        if (event.calendarId === calendar.id) {
          mappedEvents.push({
            start: event.startTime,
            end: event.endTime,
            title: event.title,
            color: calendar.color,
            id: event.id,
            classNames: "ml-3",
            extendedProps: { isBackground: true, order: 9999 + orderOffset },
          });
          orderOffset += 1;
        }
      }
    }
    onChange(mappedEvents);
  }, [events, selected, onChange]);

  const { calendars, nonSelectedCalendars } = useMemo(() => {
    const cals = data?.getCalendarsV2 ?? [];
    return {
      calendars: cals,
      nonSelectedCalendars: cals.filter(
        (cal) => !selected.find((s) => s.id === cal.id)
      ),
    };
  }, [data?.getCalendarsV2, selected]);

  const onChangeCallback = useCallback(
    (calendar: Calendar, checked: boolean) => {
      if (checked) {
        setSelected((curSel) => {
          const colorMap = sortBy(
            COLORS.map((color) => ({
              color,
              usage: curSel.filter((s) => s.color === color).length,
            })),
            ["usage", "color"]
          );
          return [...curSel, { ...calendar, color: colorMap[0].color }];
        });
        loadEvents(calendar.id);
      } else {
        setSelected((curSel) => curSel.filter((s) => s.id !== calendar.id));
        setEvents((loadedEvents) =>
          loadedEvents.filter((e) => e.calendarId !== calendar.id)
        );
      }
    },
    [loadEvents]
  );

  const noAccessCalendar =
    !userAccessData?.currentUserPrisma?.canAccessCalendar;

  return (
    <div
      className={clsx(
        className,
        "space-y-3 h-full flex flex-col pt-1 pr-1 w-64 flex-shrink-0"
      )}
    >
      {noAccessCalendar ? (
        <div className="bg-purple-50 p-5 rounded-lg">
          <h5 className="text-body-md-heavy">Connect your calendar</h5>
          <p className="text-body-md mt-1">
            Connect your Google account to view your teammates&apos;
            availability.
          </p>
          <Button
            className="w-full mt-4"
            isLoading={takingPermission}
            onClick={takeCalendarPermission}
            icon={serviceGoogle}
            variant="dark"
          >
            Connect Calendar
          </Button>
        </div>
      ) : (
        <>
          <TextField
            value={searchTerm}
            icon={atlasSearch}
            placeholder="Search calendars"
            onChange={setSearchTerm}
            isClearable
          />
          {!!selected.length && (
            <div className="flex justify-between">
              <div className="flex items-center gap-2">
                <p className="text-body-md text-subtle">
                  {strings.pluralize(
                    "selected calendar",
                    selected.length,
                    true
                  )}
                </p>
                {loadingEvents.length > 0 && <LoadingIndicator size="small" />}
              </div>

              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <Link
                className="text-body-md"
                as="button"
                onClick={() => setSelected([])}
              >
                Clear
              </Link>
            </div>
          )}
          {/* Selected calendars */}
          <div className="space-y-3 flex-shrink-0">
            {selected.map((calendar) => (
              <div key={calendar.id} className="flex items-center gap-2">
                <Checkbox
                  id={calendar.id}
                  color={calendar.color}
                  onChange={(e) =>
                    onChangeCallback(calendar, e.currentTarget.checked)
                  }
                  defaultChecked
                />
                <div
                  className="w-3 h-3 rounded-md"
                  style={{ backgroundColor: calendar.color }}
                />
                <label htmlFor={calendar.id} className="text-body-md">
                  {calendar.name}
                </label>
              </div>
            ))}
          </div>
          {!!nonSelectedCalendars.length && (
            <div className="overflow-y-scroll space-y-3 flex-grow">
              {nonSelectedCalendars.map((calendar) => (
                <div key={calendar.id} className="flex items-center gap-2">
                  <Checkbox
                    id={calendar.id}
                    color="#fff"
                    onChange={(e) =>
                      onChangeCallback(calendar, e.currentTarget.checked)
                    }
                    defaultChecked={false}
                  />
                  <div
                    className="w-3 h-3 rounded-md"
                    style={{ backgroundColor: "#fff" }}
                  />
                  <label htmlFor={calendar.id} className="text-body-md">
                    {calendar.name}
                  </label>
                </div>
              ))}
              {!loading && calendars.length === LIMIT && (
                <p className="text-body-md text-subtle text-center">
                  Max results returned. Use search above to find additional
                  calendars.
                </p>
              )}
            </div>
          )}
          {!loading && calendars.length === 0 && (
            <p className="text-body-md">No results found</p>
          )}
          {loading && (
            <div className="flex justify-center items-center h-32">
              <LoadingIndicator size="small" />
            </div>
          )}
        </>
      )}
    </div>
  );
}
